import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Table, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { ApiError, handleError } from '../../../api/base';
import UserService from '../../../api/user';
import { StreamEnvironmentModel } from '../../../models/stream-environment';
import { useAuth } from '../../../hooks/useAuth';
import { UserPermissions } from '../../../util';
import './StudioOnlineView.less';

const { Text } = Typography;

const StudioOnlineView = () => {
  const [launching, setLaunching] = useState(false);
  const [launchingEnvironmentUuid, setLaunchingEnvironmentUuid] = useState<
    string | null
  >(null);
  const [loadingEnvironments, setLoadingEnvironments] = useState(false);
  const [streamEnvironments, setStreamEnvironments] = useState<
    StreamEnvironmentModel[]
  >([]);
  const { hasPermission } = useAuth();

  const isStudioAdmin = hasPermission(UserPermissions.StudioAdmin);

  const handleLaunch = useCallback(async (streamUuid?: string) => {
    const launchTab = window.open('', '_blank', 'noopener,noreferrer');

    try {
      if (streamUuid) {
        setLaunchingEnvironmentUuid(streamUuid);
      } else {
        setLaunching(true);
      }

      const response = await UserService().getEmbedSessionUrl(streamUuid);
      const embedUrl = response?.data?.url;

      if (!embedUrl) {
        throw {
          error: {
            code: 500,
            message: 'Failed to get Studio launch URL.',
          },
        };
      }

      if (launchTab) {
        launchTab.location.href = embedUrl;
      } else {
        window.open(embedUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      if (launchTab && !launchTab.closed) {
        launchTab.close();
      }
      handleError(error as ApiError);
    } finally {
      if (streamUuid) {
        setLaunchingEnvironmentUuid(null);
      } else {
        setLaunching(false);
      }
    }
  }, []);

  const fetchEnvironments = useCallback(async () => {
    setLoadingEnvironments(true);
    try {
      const response = await UserService().getAvailableStreamEnvironments();
      setStreamEnvironments(response?.data || []);
    } catch (error) {
      handleError(error as ApiError);
    } finally {
      setLoadingEnvironments(false);
    }
  }, []);

  useEffect(() => {
    if (isStudioAdmin) {
      fetchEnvironments();
    }
  }, [fetchEnvironments, isStudioAdmin]);

  const columns: ColumnsType<StreamEnvironmentModel> = useMemo(
    () => [
      {
        title: 'Environment',
        dataIndex: 'name',
        key: 'name',
        render: (name: string) => <Text>{name}</Text>,
      },
      {
        title: 'Launch',
        key: 'launch',
        align: 'right',
        width: 220,
        render: (_, record) => (
          <Button
            type="primary"
            size="small"
            onClick={() => handleLaunch(record.uuid)}
            loading={launchingEnvironmentUuid === record.uuid}
          >
            Launch Environment
          </Button>
        ),
      },
    ],
    [handleLaunch, launchingEnvironmentUuid]
  );

  return (
    <div className="studio-online-view">
      {!isStudioAdmin ? (
        <div className="studio-online-view-cta">
          <Button
            type="primary"
            size="large"
            className="studio-online-view-cta-button"
            loading={launching}
            onClick={() => handleLaunch()}
          >
            Launch Studio
          </Button>
        </div>
      ) : (
        <div className="studio-online-view-admin">
          <div className="studio-online-view-admin-table">
            <Table
              size="small"
              rowKey={(record) => record.uuid}
              loading={loadingEnvironments}
              dataSource={streamEnvironments}
              columns={columns}
              pagination={false}
              locale={{
                emptyText: 'No active environments available.',
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default StudioOnlineView;
